import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

interface WPReportParams {
  StuSch:           string;
  StuFam:           string;
  StudentID:        string;
  StuRepNo:         string;
  StuCnt:           string;
  StudyYear:        string;
  WeekNumber:       string;
  IsSpecificfWeek:  string;
  FirstName:        string;
  COUNTRY:          string;
}

type LoadState = "idle" | "loading" | "success" | "error";

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

const REPORT_ENDPOINT = `${API_BASE_URL}api/weekly-plan/`;

function getClientHeight(): number {
  return (
    window.innerHeight ||
    document.documentElement?.clientHeight ||
    document.body?.clientHeight ||
    600
  );
}

function parseParams(): WPReportParams {
  const qs = new URLSearchParams(window.location.search);
  return {
    StuSch:          qs.get("StuSch")          ?? "",
    StuFam:          qs.get("StuFam")          ?? "",
    StudentID:       qs.get("StudentID")       ?? "",
    StuRepNo:        qs.get("StuRepNo")        ?? "0",
    StuCnt:          qs.get("StuCnt")          ?? "",
    StudyYear:       qs.get("StudyYear")       ?? "",
    WeekNumber:      qs.get("WeekNumber")      ?? "26",
    IsSpecificfWeek: qs.get("IsSpecificfWeek") ?? "False",
    FirstName:       qs.get("FirstName")       ?? "",
    COUNTRY:         qs.get("COUNTRY")         ?? "",
  };
}

function buildApiUrl(p: WPReportParams): string {
  const qs = new URLSearchParams();
  (Object.keys(p) as Array<keyof WPReportParams>).forEach((k) => {
    if (p[k] !== "") qs.set(k, p[k]);
  });
  return `${REPORT_ENDPOINT}?${qs.toString()}`;
}

function clampStudyYear(p: WPReportParams): WPReportParams {
  const yr = parseInt(p.StudyYear || "0", 10);
  if (!isNaN(yr) && yr > 10) {
    return { ...p, StudyYear: "10" };
  }
  return p;
}


const MD_WPReport_PDF: React.FC = () => {
  const rawParams  = useRef<WPReportParams>(parseParams());
  const params     = useRef<WPReportParams>(clampStudyYear(rawParams.current));
  const blobUrlRef = useRef<string | null>(null);

  const [loadState,  setLoadState]  = useState<LoadState>("idle");
  const [errorMsg,   setErrorMsg]   = useState<string>("");
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [iframeH,    setIframeH]    = useState<number>(getClientHeight() - 35);

  const handleResize = useCallback(() => {
    setIframeH(getClientHeight() - 35);
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  const validate = useCallback((): string | null => {
    const p = params.current;
    if (!p.StuSch)    return "Missing required parameter: StuSch (SchoolCode).";
    if (!p.StuFam)    return "Missing required parameter: StuFam (FamilyCode).";
    if (!p.StudentID) return "Missing required parameter: StudentID (Login).";
    if (!p.StuRepNo || p.StuRepNo === "0")
      return "Missing required parameter: StuRepNo (ReportNo).";
    return null;
  }, []);

  const fetchReport = useCallback(async () => {
    const validErr = validate();
    if (validErr) {
      setLoadState("error");
      setErrorMsg(validErr);
      return;
    }

    setLoadState("loading");
    setErrorMsg("");

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
      setPdfBlobUrl(null);
    }

    try {
      const url = buildApiUrl(params.current);

      const response = await fetch(url, {
        method:      "GET",
        credentials: "include",   
        headers: {
          Accept: "application/pdf, */*",
        },
      });

      if (!response.ok) {
        let serverMsg = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const text = await response.text();
          if (text) serverMsg = text;
        } catch (_) { /* ignore */ }
        throw new Error(serverMsg);
      }

      const contentType = response.headers.get("Content-Type") ?? "";
      if (!contentType.includes("application/pdf")) {
        const body = await response.text().catch(() => "(unreadable)");
        throw new Error(
          `Expected PDF but got "${contentType}".\n${body}`
        );
      }

      const blob   = await response.blob();
      const objUrl = URL.createObjectURL(blob);
      blobUrlRef.current = objUrl;

      setPdfBlobUrl(objUrl);
      setLoadState("success");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setLoadState("error");
      setErrorMsg(msg);
    }
  }, [validate]);

  useEffect(() => {
    fetchReport();
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, [fetchReport]);

  const handleCloseWindow = useCallback(() => {
    window.close();
  }, []);

  return (
    <div style={styles.page}>

      {/* ── Loading ── */}
      {loadState === "loading" && (
        <div style={styles.overlay}>
          <Spinner />
          <p style={styles.loadingTitle}>Generating Weekly Plan Report…</p>
          <p style={styles.loadingSub}>
            Student:&nbsp;<strong>{params.current.FirstName || params.current.StudentID}</strong>
            &nbsp;|&nbsp;
            Week:&nbsp;<strong>{params.current.WeekNumber}</strong>
            &nbsp;|&nbsp;
            Year:&nbsp;<strong>{params.current.StudyYear}</strong>
          </p>
        </div>
      )}

      {loadState === "error" && (
        <div style={styles.overlay}>
          <div style={styles.alertBox}>
            {/* Alert icon */}
            <div style={styles.alertIcon}>!</div>
            <h2 style={styles.alertTitle}>Report Error</h2>
            <pre style={styles.alertMsg}>{errorMsg}</pre>
            <div style={styles.alertButtons}>
              {/* Retry button */}
              <button style={styles.btnRetry} onClick={fetchReport}>
                ↺&nbsp;Retry
              </button>
      
              <button style={styles.btnClose} onClick={handleCloseWindow}>
                ✕&nbsp;Close Window
              </button>
            </div>
          </div>
        </div>
      )}

      {loadState === "success" && pdfBlobUrl && (
        <iframe
          id="ifrmWPReport"
          src={pdfBlobUrl}
          title="Weekly Plan Report"
          style={{ ...styles.iframe, height: `${iframeH}px` }}
        />
      )}
    </div>
  );
};
const Spinner: React.FC = () => <div style={spinnerStyle} />;

const styles: Record<string, React.CSSProperties> = {
  page: {
    position:   "relative",
    width:      "100%",
    height:     "100vh",
    margin:     0,
    padding:    0,
    boxSizing:  "border-box",
    background: "#f0f0eb",
    fontFamily: "Helvetica, Arial, sans-serif",
  },
  overlay: {
    position:        "absolute",
    inset:           0,
    display:         "flex",
    flexDirection:   "column",
    alignItems:      "center",
    justifyContent:  "center",
    gap:             14,
    background:      "rgba(255,255,255,0.9)",
  },
  loadingTitle: {
    margin:     0,
    fontSize:   17,
    fontWeight: 700,
    color:      "#222",
  },
  loadingSub: {
    margin:   0,
    fontSize: 13,
    color:    "#555",
  },

  alertBox: {
    background:   "#fff",
    border:       "2px solid #c0392b",
    borderRadius: 10,
    padding:      "32px 40px",
    maxWidth:     540,
    width:        "88%",
    textAlign:    "center",
    boxShadow:    "0 6px 32px rgba(0,0,0,0.18)",
  },
  alertIcon: {
    width:           56,
    height:          56,
    borderRadius:    "50%",
    background:      "#c0392b",
    color:           "#fff",
    fontSize:        32,
    fontWeight:      900,
    display:         "flex",
    alignItems:      "center",
    justifyContent:  "center",
    margin:          "0 auto 14px",
  },
  alertTitle: {
    margin:     "0 0 10px",
    fontSize:   20,
    fontWeight: 700,
    color:      "#c0392b",
  },
  alertMsg: {
    fontSize:   13,
    color:      "#333",
    whiteSpace: "pre-wrap",
    wordBreak:  "break-word",
    background: "#fdf3f3",
    borderRadius: 6,
    padding:    "10px 14px",
    margin:     "0 0 22px",
    textAlign:  "left",
    maxHeight:  200,
    overflowY:  "auto",
    fontFamily: "inherit",
  },
  alertButtons: {
    display:        "flex",
    gap:            12,
    justifyContent: "center",
  },
  btnRetry: {
    padding:         "10px 22px",
    fontSize:        14,
    fontWeight:      600,
    background:      "#e67e22",
    color:           "#fff",
    border:          "none",
    borderRadius:    7,
    cursor:          "pointer",
  },
  /* Close Window button — direct equivalent of window.close() */
  btnClose: {
    padding:         "10px 22px",
    fontSize:        14,
    fontWeight:      600,
    background:      "#fff",
    color:           "#c0392b",
    border:          "2px solid #c0392b",
    borderRadius:    7,
    cursor:          "pointer",
  },
  iframe: {
    display: "block",
    width:   "100%",
    border:  "none",
  },
};

const spinnerStyle: React.CSSProperties = {
  width:        52,
  height:       52,
  borderRadius: "50%",
  border:       "5px solid #ddd",
  borderTop:    "5px solid #e67e22",
  animation:    "mdwp-spin 0.85s linear infinite",
};

if (typeof document !== "undefined" && !document.getElementById("mdwp-spin-style")) {
  const s  = document.createElement("style");
  s.id     = "mdwp-spin-style";
  s.textContent = "@keyframes mdwp-spin { to { transform: rotate(360deg); } }";
  document.head.appendChild(s);
}

export default MD_WPReport_PDF;